/**
 * useInstallPrompt — install-to-home-screen eligibility + action.
 *
 * Reports WHETHER the app can be installed right now; it has no opinion on
 * WHEN it's polite to ask (that's the caller's `armed` decision — see
 * <InstallPrompt>). Two paths:
 *
 *   - Chromium-family (Android, desktop): a `beforeinstallprompt` event was
 *     captured by src/lib/installPrompt.ts. `promptInstall()` fires the
 *     native install dialog.
 *   - iOS Safari: `beforeinstallprompt` never fires there. We detect iOS
 *     Safari (and that we're not already running standalone) and surface
 *     `isIOSFallback` so the caller can show "Tap Share, then Add to Home
 *     Screen" instructions instead of a button.
 *
 * Dismissal is sticky across sessions via src/lib/preferences.ts (the
 * Capacitor-ready storage-adapter seam — swaps to @capacitor/preferences
 * post-wrap, never touch localStorage directly here).
 */

import {
  clearDeferredInstallEvent,
  getDeferredInstallEvent,
  subscribeInstallEvent,
} from '@/lib/installPrompt';
import { getPreference, setPreference } from '@/lib/preferences';
import { useCallback, useState, useSyncExternalStore } from 'react';

const DISMISSED_KEY = 'install-prompt-dismissed';

function isStandaloneDisplay(): boolean {
  if (typeof window === 'undefined') return false;
  const navigatorStandalone = (navigator as unknown as { standalone?: boolean }).standalone;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches === true ||
    navigatorStandalone === true
  );
}

function isIOSSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIOSDevice = /iphone|ipad|ipod/i.test(ua);
  // Exclude other iOS browsers (Chrome/Firefox on iOS still use WebKit but
  // don't support add-to-home-screen the same way and shouldn't get this copy).
  const isOtherIOSBrowser = /crios|fxios|edgios|opios/i.test(ua);
  return isIOSDevice && !isOtherIOSBrowser;
}

export interface UseInstallPromptResult {
  /** True when a `beforeinstallprompt` event is ready to fire via `promptInstall()`. */
  canInstall: boolean;
  /** True on iOS Safari (not already installed) — show manual instructions, there is no programmatic prompt. */
  isIOSFallback: boolean;
  /** True once the user has dismissed the prompt (persisted; sticky forever, this is a once-only ask). */
  dismissed: boolean;
  /** Fires the native install dialog. No-op if `canInstall` is false. */
  promptInstall: () => Promise<void>;
  /** Persists the dismissal so the prompt never shows again. */
  dismiss: () => void;
}

export function useInstallPrompt(): UseInstallPromptResult {
  const deferredEvent = useSyncExternalStore(
    subscribeInstallEvent,
    getDeferredInstallEvent,
    () => null,
  );
  // Read once on mount (lazy initializer) — dismiss() below flips this
  // synchronously within the same component instance so the bar hides
  // immediately, in addition to persisting for future sessions/mounts.
  const [dismissed, setDismissed] = useState(() => getPreference(DISMISSED_KEY) === 'true');

  const alreadyInstalled = isStandaloneDisplay();

  const promptInstall = useCallback(async () => {
    const event = getDeferredInstallEvent();
    if (!event) return;
    await event.prompt();
    // The event is single-use regardless of outcome (accepted or dismissed).
    clearDeferredInstallEvent();
  }, []);

  const dismiss = useCallback(() => {
    setPreference(DISMISSED_KEY, 'true');
    setDismissed(true);
  }, []);

  return {
    canInstall: !alreadyInstalled && deferredEvent !== null,
    isIOSFallback: !alreadyInstalled && deferredEvent === null && isIOSSafari(),
    dismissed,
    promptInstall,
    dismiss,
  };
}
